import type {
    AbstractControl,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';

/**
 * Custom email validator that enforces TLD requirements.
 *
 * This validator ensures emails follow the pattern: local@domain.tld
 * where a top-level domain is required. This prevents downstream
 * email delivery failures and ensures consistency across the platform.
 *
 * Examples:
 * - ✅ Valid: user@example.com, test@domain.org
 * - ❌ Invalid: user@example, test@localhost
 *
 * @returns ValidatorFn that returns null for valid emails or { email: true } for invalid ones
 */
export function emailValidator(): ValidatorFn {
    const check = emailCheck();
    return (control: AbstractControl): ValidationErrors | null =>
        check(control.value);
}

/** Value-based validation shared by Reactive and Signal Forms. */
export function emailCheck(): (value: unknown) => ValidationErrors | null {
    return (value: unknown): ValidationErrors | null => {
        if (!value) return null;
        const emailValue = String(value).trim();
        return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
            emailValue
        )
            ? null
            : { email: true };
    };
}
